import * as React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  InputLabel,
  Modal,
  Button,
  CircularProgress,
  List,
  ListItem,
} from "@mui/material";
import { Grid } from "@mui/material";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import CloseIcon from "@mui/icons-material/Close";
import DatePicker from "react-datepicker";

import "react-datepicker/dist/react-datepicker.css";
import { useState, useEffect, useContext } from "react";

import { ClientProfile } from "./ClientProfile";
import { EditClientInformation } from "./EditClientInformation";
import InvoiceInformation from "./InvoiceInformation";
import { RecommendedProducts } from "./RecommendedProducts";
import { CustomInput } from "Components/Form";
import { Header } from "Components/Header";
import { Colors } from "Utils/colors";
import { LocalImage } from "Utils/images";
import { Strings } from "Utils/strings";
import { useNavigate } from "react-router-dom";
import { getAccessToken } from "Features/slice/customer/customer.Slice";
import { useAppDispatch, useAppSelector } from "Hooks/reduxHook";
import { Customer } from "Features/services/customer.api";
import { SnackBarContext } from "Context/SnackContext";
import { Invoices } from "Features/services/invoice.api";
import InvoiceDetailModal from "./InvoiceDetailModal";

const style = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-40%, -50%)",
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
  borderRadius: "12px",
  width: "75%",
  height: "90%",
};

export default function Invoice() {
  
  const navigate = useNavigate();
  const { handleSnackbar } = useContext(SnackBarContext);

  const [trackStatusUpdated, setTrackStatusUpdated] = useState<boolean>(false)

  const [invoiceResult, setInvoiceResult] = useState([]);
  const [invoiceDropdown, setInvoiceDropdown] = useState(false);
  const [loading, setLoading] = useState(false);

  const [invoiceDetail, setInvoiceDetail] = useState<any>()
  const [invoiceModal, setInvoiceModal] = useState<boolean>(false);

  const openInvoiceModal = () => {
    setInvoiceModal(true);
  };

  const closeInvoiceModal = () => {
    setInvoiceModal(false);
  };

  const dispatch = useAppDispatch();
  const { access_token } = useAppSelector((state: any) => state.customer);

  const [open, setOpen] = useState(false);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const [invoiceCount, setInvoiceCount] = useState({
    pendingPaymentCount: 0,
    recentInvoicesCount: 0,
    draftInvoiceCount: 0,
  });

  const handleCardClick = (type: any) => {
    navigate("/invoice/invoiceDetails", { state: { type } });
  };

  useEffect(() => {
    dispatch(getAccessToken());
  }, []);

  const fetchInvoiceCount = async () => {
    const payload = { access_token };

    try {
      const response = await Invoices.instance.invoicesCount(payload);
      setInvoiceCount(response?.data?.data);
    } catch (error: any) {
      console.error("Error fetching data:", error);
      handleSnackbar(true, `${error.message}`, "error");
    }
  };

  useEffect(() => {
    fetchInvoiceCount();
  }, [access_token]);

  const handleSearchInvoice = async (value: string) => {
    const payload = {
      access_token: access_token,
      DocNumber: value,
    };

    try {
      setLoading(true);
      const response = await Invoices.instance.inputSearch(payload);
      const data = response.data.data;
      setInvoiceResult(data);
      setInvoiceDropdown(true);
    } catch (error) {
      console.error("Something went wrong", error);
    } finally {
      setLoading(false);
    }
  };

  const handleInvoiceClick = async (invoiceId: any) => {

      const payload = {
        access_token,
        invoiceId,
      };

      try {
        setLoading(true)
        const response = await Invoices.instance.invoiceDetail(payload);
        setInvoiceDetail(response.data);
        setInvoiceModal(true);
      } catch (error: any) {
        handleSnackbar(true, error?.message || "An error occurred");
        console.error("Error:", error);
      } finally {
        setLoading(false)
      }
    setInvoiceDropdown(false);
  };

  return (
    <Box
      component="main"
      sx={{
        backgroundImage: `url(${LocalImage.contactBg})`,
        backgroundRepeat: "no-repeat",
        backgroundColor: `${Colors.white}`,
        backgroundSize: "20% auto",
        backgroundPosition: "right bottom",
        flex: 1,
        overflow: "auto",
        px: 3,
        pt: 15,
        pb: 8,
      }}
    >
      <Header
        title={"Invoice Creation"}
        subTitle="Effortlessly Manage and Generate Your Invoices"
        onInputChange={handleSearchInvoice}
      />

      <Box sx={{ position: "relative", marginTop: 1 }}>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: '10vh',
            }}
          >
            <CircularProgress size={24} />
          </Box>
        ) : (
          invoiceDropdown && (
            <Box
              sx={{
                position: "absolute",
                top: "100%",
                left: 0,
                right: 0,
                bgcolor: "#fff",
                borderRadius: "0 0 25px 25px",
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                zIndex: 10,
                maxHeight: "200px",
                overflowY: "scroll",
              }}
            >
              <List>
                {invoiceResult.length > 0 ? (
                  invoiceResult.map((invoice: any) =>
                    invoice.Line.map((line: any) =>
                        line.DetailType === "SalesItemLineDetail" && (
                          <ListItem
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                            }}
                            button
                            key={line.Id}
                            onClick={() => handleInvoiceClick(invoice.Id)}
                          >
                            <Typography>
                              {line.SalesItemLineDetail.ItemRef.name || "N/A"}
                            </Typography>
                            <Typography>View</Typography>
                          </ListItem>
                        )
                    )
                  )
                ) : (
                  <ListItem>
                    <Typography>No results found</Typography>
                  </ListItem>
                )}
              </List>
            </Box>
          )
        )}
      </Box>

      <Box sx={{ width: "100%" }}>
        <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
          <Grid item sm={12} md={12} lg={7}>
            <Box>
              <Box className="card-main-box">
                <Card
                  onClick={() => navigate("/invoice/draftList")}
                  className="card-style-01"
                >
                  <Box className="card-style-02">
                    <CardContent className="card-style-03" sx={{ p: 1 }}>
                      <Typography variant="body2">
                        {Strings.savedDrafts}
                      </Typography>
                      <Typography>{invoiceCount.draftInvoiceCount}</Typography>
                    </CardContent>
                    <Box className="arrow-style-01">
                      <Box className="arrow-style-02">
                        <img
                          style={{ width: "60%" }}
                          className="arrow-style-03"
                          alt="WG Fabricators logo"
                          src={LocalImage.iconLinearExport}
                        />
                      </Box>
                    </Box>
                  </Box>
                </Card>

                <Card
                  onClick={() => handleCardClick("recentlyCompleted")}
                  className="card-style-01"
                >
                  <Box className="card-style-02">
                    <CardContent className="card-style-03" sx={{ p: 1 }}>
                      <Typography variant="body2">
                        {Strings.recentlyCompleted}
                      </Typography>
                      <Typography>
                        {invoiceCount.recentInvoicesCount}
                      </Typography>
                    </CardContent>
                    <Box className="arrow-style-01">
                      <Box className="arrow-style-02">
                        <img
                          style={{ width: "60%" }}
                          className="arrow-style-03"
                          alt="WG Fabricators logo"
                          src={LocalImage.iconLinearExport}
                        />
                      </Box>
                    </Box>
                  </Box>
                </Card>

                <Card
                  onClick={() => handleCardClick("pendingPayment")}
                  className="card-style-01"
                >
                  <Box className="card-style-02">
                    <CardContent className="card-style-03" sx={{ p: 1 }}>
                      <Typography variant="body2">
                        {Strings.pendingPayment}
                      </Typography>
                      <Typography>
                        {invoiceCount.pendingPaymentCount}
                      </Typography>
                    </CardContent>
                    <Box
                      onClick={() => navigate("/invoice/invoiceDetails")}
                      className="arrow-style-01"
                    >
                      <Box className="arrow-style-02">
                        <img
                          style={{ width: "60%" }}
                          className="arrow-style-03"
                          alt="WG Fabricators logo"
                          src={LocalImage.iconLinearExport}
                        />
                      </Box>
                    </Box>
                  </Box>
                </Card>
              </Box>

              <InvoiceInformation />
            </Box>
          </Grid>

          <Grid item sm={12} md={12} lg={5}>
            
            <ClientProfile openEditModal={handleOpen} />

            {/* <RecommendedProducts /> */}
          </Grid>
        </Grid>
      </Box>

      <InvoiceDetailModal
      invoiceModal={invoiceModal}
      closeInvoiceModal={closeInvoiceModal}
      invoiceDetail={invoiceDetail}
      setTrackStatusUpdated={setTrackStatusUpdated}
      />
      
    </Box>
  );
}
